import { useState, useEffect, useRef, useContext } from 'react';
import ParticleSphereAnimation from '@/components/ui/input-otp-10-utils/particalsphear';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { OTPInput, OTPInputContext } from 'input-otp';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { ShieldCheck } from 'lucide-react';

const SPRING_TRANSITION = {
  type: 'spring',
  stiffness: 450,
  damping: 28,
} as const;

const CustomOTPSlot = ({
  index,
  isSuccess,
}: {
  index: number;
  isSuccess: boolean;
}) => {
  const inputOTPContext = useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  const [pulseKey, setPulseKey] = useState(0);
  const prevCharRef = useRef(char);

  useEffect(() => {
    if (char && char !== prevCharRef.current) {
      setPulseKey((prev) => prev + 1);
    }
    prevCharRef.current = char;
  }, [char]);

  return (
    <div
      className={cn(
        'relative flex h-14 w-11 sm:w-12 items-center justify-center rounded-xl border transition-all duration-300 font-mono text-xl font-bold select-none',
        isSuccess
          ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
          : isActive
            ? 'border-blue-600 bg-blue-50 text-slate-900 dark:border-blue-400 dark:bg-blue-950/40 dark:text-white'
            : 'border-slate-200 bg-white text-slate-900 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'
      )}
    >
      <AnimatePresence mode="popLayout">
        {char ? (
          <motion.span
            key={`char-${char}`}
            initial={{ opacity: 0, scale: 0.5, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: -6 }}
            transition={SPRING_TRANSITION}
            className={cn(
              'absolute font-mono text-xl font-bold',
              isSuccess
                ? 'text-emerald-700 dark:text-emerald-400'
                : 'text-slate-900 dark:text-white'
            )}
          >
            {char}
          </motion.span>
        ) : null}
      </AnimatePresence>
      <AnimatePresence>
        {pulseKey > 0 && (
          <motion.div
            key={pulseKey}
            className="absolute inset-0 rounded-xl border border-blue-500 pointer-events-none"
            initial={{ opacity: 0.8, scale: 0.9, filter: 'blur(0px)' }}
            animate={{ opacity: 0, scale: 1.5, filter: 'blur(2px)' }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>
      {hasFakeCaret && !isSuccess && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <motion.div
            className="h-6 w-0.5 bg-blue-600 dark:bg-blue-400"
            animate={{ opacity: [1, 0, 1] }}
            transition={{
              repeat: Infinity,
              duration: 1,
              ease: 'easeInOut',
            }}
          />
        </div>
      )}
    </div>
  );
};

export type InputOtp10Props = {
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (code: string) => void;
  isSuccess?: boolean;
  isVerifying?: boolean;
  error?: string | null;
  title?: string;
  description?: string;
  successTitle?: string;
  successDescription?: string;
  rememberDevice?: boolean;
  onRememberDeviceChange?: (checked: boolean) => void;
  showRememberDevice?: boolean;
  disabled?: boolean;
};

export default function InputOtp10({
  value: controlledValue,
  onChange,
  onComplete,
  isSuccess = false,
  isVerifying = false,
  error = null,
  title = 'Enter authenticator code',
  description = 'Open your authenticator app and enter the 6-digit code.',
  successTitle = 'Verified',
  successDescription = 'Authenticator code accepted. Continuing…',
  rememberDevice = false,
  onRememberDeviceChange,
  showRememberDevice = true,
  disabled = false,
}: InputOtp10Props) {
  const [uncontrolledValue, setUncontrolledValue] = useState('');
  const value = controlledValue ?? uncontrolledValue;
  const completedRef = useRef<string | null>(null);

  const setValue = (next: string) => {
    if (controlledValue === undefined) setUncontrolledValue(next);
    onChange?.(next);
  };

  useEffect(() => {
    if (value.length === 6 && completedRef.current !== value && !isVerifying && !isSuccess) {
      completedRef.current = value;
      onComplete?.(value);
    }
    if (value.length < 6) {
      completedRef.current = null;
    }
  }, [value, onComplete, isVerifying, isSuccess]);

  return (
    <div className="relative w-full max-w-sm sm:max-w-md mx-auto rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm overflow-hidden group select-none dark:border-slate-700 dark:bg-slate-900">
      <div className="absolute -top-12 -right-12 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none" />
      <div className="relative flex flex-col items-center gap-6 sm:gap-7">
        <div className="relative w-40 h-40 flex items-center justify-center">
          <div
            className="absolute inset-0 rounded-full border border-dashed border-blue-300/60 animate-spin pointer-events-none dark:border-blue-500/30"
            style={{ animationDuration: '60s' }}
          />
          <div
            className="absolute inset-2 rounded-full border border-blue-200/70 animate-spin pointer-events-none dark:border-blue-500/20"
            style={{ animationDuration: '30s', animationDirection: 'reverse' }}
          />
          <div className="absolute inset-4 rounded-full border border-dashed border-slate-200 pointer-events-none dark:border-slate-700" />
          <div
            className={cn(
              'absolute -inset-1.5 rounded-full transition-all duration-500',
              isSuccess
                ? 'bg-gradient-to-t from-emerald-500/0 via-emerald-500/10 to-emerald-500/0 animate-pulse'
                : 'bg-gradient-to-t from-blue-500/0 via-blue-500/5 to-blue-500/0 animate-pulse'
            )}
            style={!isSuccess ? { animationDuration: '3s' } : undefined}
          />
          <div
            className={cn(
              'w-32 h-32 rounded-full overflow-hidden flex items-center justify-center transition-all duration-500 bg-slate-50 dark:bg-slate-950',
              isSuccess ? 'scale-105' : 'scale-95 group-hover:scale-100'
            )}
          >
            <ParticleSphereAnimation className="w-full h-full scale-135 opacity-90 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
          <AnimatePresence>
            {isSuccess && (
              <motion.div
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.6 }}
                className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[2px] rounded-full dark:bg-slate-900/70"
              >
                <div className="p-3 rounded-full bg-emerald-100 border border-emerald-300 text-emerald-600 dark:bg-emerald-500/20 dark:border-emerald-500/40 dark:text-emerald-400">
                  <ShieldCheck className="w-8 h-8" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="text-center flex flex-col gap-1.5">
          <h3 className="text-sm font-bold tracking-tight text-slate-900 uppercase dark:text-white">
            {isSuccess ? successTitle : title}
          </h3>
          <p className="text-xs text-slate-500 max-w-[16rem] leading-relaxed mx-auto dark:text-slate-400">
            {isSuccess ? successDescription : description}
          </p>
        </div>
        <div className="w-full flex justify-center">
          <OTPInput
            maxLength={6}
            value={value}
            onChange={setValue}
            pattern={REGEXP_ONLY_DIGITS}
            disabled={disabled || isVerifying || isSuccess}
            containerClassName="group flex items-center justify-center gap-2 sm:gap-2.5"
          >
            <div className="flex items-center gap-2 sm:gap-2.5">
              {Array.from({ length: 6 }).map((_, idx) => (
                <CustomOTPSlot key={idx} index={idx} isSuccess={isSuccess} />
              ))}
            </div>
          </OTPInput>
        </div>
        {error ? (
          <p className="text-xs text-center text-red-600 dark:text-red-400">{error}</p>
        ) : (
          <div className="h-4 flex items-center justify-center">
            <span className="text-xs font-mono text-slate-400 uppercase tracking-widest dark:text-slate-500">
              {isVerifying
                ? 'Verifying code…'
                : value.length === 0
                  ? 'Awaiting code…'
                  : value.length < 6
                    ? `Entered ${value.length} / 6`
                    : isSuccess
                      ? 'Code accepted'
                      : 'Ready to verify'}
            </span>
          </div>
        )}
        {showRememberDevice && onRememberDeviceChange ? (
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none dark:text-slate-400">
            <input
              type="checkbox"
              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              checked={rememberDevice}
              disabled={disabled || isVerifying}
              onChange={(e) => onRememberDeviceChange(e.target.checked)}
            />
            Remember this device for 5 days
          </label>
        ) : null}
      </div>
    </div>
  );
}
